import React, { useState, useEffect, useCallback } from 'react';

const GA4Analytics = () => {
  const [data, setData] = useState(null);
  const [keywords, setKeywords] = useState(null);
  const [keywordsLoading, setKeywordsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('overview');
  const token = localStorage.getItem('adminToken');

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ga4/dashboard?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to fetch GA4 data');
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  const fetchKeywords = useCallback(async () => {
    setKeywordsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/search-console/keywords?days=${days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Failed to fetch keywords');
      }
      const result = await res.json();
      setKeywords(result);
    } catch (err) {
      console.error('Keywords fetch error:', err);
      setKeywords({ keywords: [], error: err.message });
    } finally {
      setKeywordsLoading(false);
    }
  }, [days, token]);

  useEffect(() => {
    fetchDashboard();
    fetchKeywords();
  }, [fetchDashboard, fetchKeywords]);

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 1) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Google Analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Analytics</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <div className="text-sm text-gray-600 mb-4">
          <p>Please ensure:</p>
          <ul className="list-disc list-inside text-left max-w-md mx-auto mt-2">
            <li>The service account has been added to your GA4 property as a Viewer</li>
            <li>The Google Analytics Data API is enabled in your Google Cloud project</li>
            <li>The GA4 Property ID (303087554) is correct</li>
          </ul>
        </div>
        <button
          onClick={fetchDashboard}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  const overview = data?.overview || {};
  const trafficSources = data?.traffic_sources || [];
  const topPages = data?.top_pages || [];
  const devices = data?.devices || [];
  const topCities = data?.top_cities || [];

  // Calculate total sessions for percentages
  const totalTrafficSessions = trafficSources.reduce((sum, s) => sum + s.sessions, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">📊 Google Analytics</h1>
          <p className="text-gray-600 text-sm mt-1">Real-time website analytics from GA4</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchDashboard}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <span className="animate-spin">⟳</span>
            ) : (
              <span>🔄</span>
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Users"
          value={formatNumber(overview.total_users)}
          icon="👥"
          color="blue"
        />
        <StatCard
          label="Sessions"
          value={formatNumber(overview.sessions)}
          icon="📈"
          color="green"
        />
        <StatCard
          label="Page Views"
          value={formatNumber(overview.page_views)}
          icon="👁️"
          color="purple"
        />
        <StatCard
          label="Bounce Rate"
          value={`${overview.bounce_rate || 0}%`}
          icon="↩️"
          color="orange"
        />
        <StatCard
          label="Avg. Duration"
          value={formatDuration(overview.avg_session_duration)}
          icon="⏱️"
          color="teal"
        />
        <StatCard
          label="New Users"
          value={formatNumber(overview.new_users)}
          icon="✨"
          color="pink"
        />
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'keywords', label: 'Keywords', icon: '🔍' },
            { id: 'traffic', label: 'Traffic Sources', icon: '🌐' },
            { id: 'pages', label: 'Top Pages', icon: '📄' },
            { id: 'audience', label: 'Audience', icon: '👥' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Types */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">👤 User Breakdown</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">New Users</span>
                <span className="font-semibold text-green-600">{formatNumber(overview.new_users)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full"
                  style={{
                    width: `${overview.total_users > 0 ? (overview.new_users / overview.total_users) * 100 : 0}%`
                  }}
                ></div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Returning Users</span>
                <span className="font-semibold text-blue-600">{formatNumber(overview.returning_users)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{
                    width: `${overview.total_users > 0 ? (overview.returning_users / overview.total_users) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Devices */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">📱 Devices</h3>
            <div className="space-y-3">
              {devices.map((device, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">
                      {device.device === 'mobile' ? '📱' : device.device === 'desktop' ? '🖥️' : '📟'}
                    </span>
                    <span className="text-gray-700 capitalize">{device.device}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">{formatNumber(device.sessions)} sessions</span>
                    <span className="font-semibold text-teal-600">{device.percentage}%</span>
                  </div>
                </div>
              ))}
              {devices.length === 0 && (
                <p className="text-gray-500 text-center py-4">No device data available</p>
              )}
            </div>
          </div>

          {/* Top Cities */}
          <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
            <h3 className="font-semibold text-gray-800 mb-4">📍 Top Cities</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {topCities.map((city, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-3">
                  <div className="font-medium text-gray-800">{city.city}</div>
                  <div className="text-sm text-gray-500">{formatNumber(city.sessions)} sessions</div>
                </div>
              ))}
              {topCities.length === 0 && (
                <p className="text-gray-500 col-span-4 text-center py-4">No geographic data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'keywords' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">🔍 Top Search Keywords</h3>
            <p className="text-sm text-gray-500">Keywords people use to find your website in Google Search</p>
          </div>
          {keywordsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          ) : keywords?.error ? (
            <div className="p-6 text-center">
              <p className="text-red-600 mb-2">Failed to load keywords</p>
              <p className="text-sm text-gray-500">{keywords.error}</p>
              <p className="text-xs text-gray-400 mt-2">
                Make sure the service account has access to Search Console
              </p>
            </div>
          ) : keywords?.keywords?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">#</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Keyword</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Clicks</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Impressions</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">CTR</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Avg. Position</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.keywords.map((kw, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-400 text-sm">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-800">{kw.keyword}</span>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-teal-600">
                        {formatNumber(kw.clicks)}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-600">
                        {formatNumber(kw.impressions)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${
                          kw.ctr > 5 ? 'text-green-600' :
                          kw.ctr > 2 ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {kw.ctr}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${
                          kw.position <= 3 ? 'text-green-600' :
                          kw.position <= 10 ? 'text-blue-600' :
                          kw.position <= 20 ? 'text-orange-600' : 'text-gray-600'
                        }`}>
                          {kw.position}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p>No keyword data available for this period</p>
              <p className="text-sm mt-2">Keywords will appear after search traffic is recorded</p>
            </div>
          )}
          {keywords?.total_keywords > 0 && (
            <div className="p-4 border-t bg-gray-50 text-sm text-gray-500 text-center">
              Showing top {keywords.keywords.length} of {keywords.total_keywords} keywords • Data from Google Search Console
            </div>
          )}
        </div>
      )}

      {activeTab === 'traffic' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">🌐 Traffic Sources</h3>
            <p className="text-sm text-gray-500">Where your visitors are coming from</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Channel</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Sessions</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Users</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Share</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Visual</th>
                </tr>
              </thead>
              <tbody>
                {trafficSources.map((source, idx) => {
                  const percentage = totalTrafficSessions > 0
                    ? ((source.sessions / totalTrafficSessions) * 100).toFixed(1)
                    : 0;
                  return (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {source.channel === 'Organic Search' ? '🔍' :
                             source.channel === 'Direct' ? '🔗' :
                             source.channel === 'Referral' ? '↗️' :
                             source.channel === 'Organic Social' ? '📱' :
                             source.channel === 'Paid Search' ? '💰' :
                             source.channel === 'Email' ? '📧' : '🌐'}
                          </span>
                          <span className="font-medium text-gray-800">{source.channel}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-medium">{formatNumber(source.sessions)}</td>
                      <td className="py-3 px-4 text-right text-gray-600">{formatNumber(source.users)}</td>
                      <td className="py-3 px-4 text-right text-teal-600 font-semibold">{percentage}%</td>
                      <td className="py-3 px-4 w-32">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-teal-500 h-2 rounded-full"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {trafficSources.length === 0 && (
              <p className="text-gray-500 text-center py-8">No traffic source data available</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'pages' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">📄 Top Performing Pages</h3>
            <p className="text-sm text-gray-500">Your most viewed pages</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Page</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Views</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Users</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Bounce Rate</th>
                </tr>
              </thead>
              <tbody>
                {topPages.map((page, idx) => (
                  <tr key={idx} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-800 truncate max-w-xs" title={page.title}>
                        {page.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-xs" title={page.path}>
                        {page.path}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-teal-600">
                      {formatNumber(page.views)}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-600">{formatNumber(page.users)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-medium ${
                        page.bounce_rate > 70 ? 'text-red-600' :
                        page.bounce_rate > 50 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {page.bounce_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {topPages.length === 0 && (
              <p className="text-gray-500 text-center py-8">No page data available</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'audience' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Devices Detailed */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">📱 Device Distribution</h3>
            <div className="space-y-4">
              {devices.map((device, idx) => (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-700 capitalize flex items-center gap-2">
                      <span className="text-xl">
                        {device.device === 'mobile' ? '📱' : device.device === 'desktop' ? '🖥️' : '📟'}
                      </span>
                      {device.device}
                    </span>
                    <span className="text-gray-600">{device.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full ${
                        device.device === 'mobile' ? 'bg-blue-500' :
                        device.device === 'desktop' ? 'bg-green-500' : 'bg-purple-500'
                      }`}
                      style={{ width: `${device.percentage}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatNumber(device.sessions)} sessions • {formatNumber(device.users)} users
                  </div>
                </div>
              ))}
              {devices.length === 0 && (
                <p className="text-gray-500 text-center py-4">No device data available</p>
              )}
            </div>
          </div>

          {/* Geographic */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-800 mb-4">📍 Geographic Distribution</h3>
            <div className="space-y-3">
              {topCities.map((city, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-800">{city.city}</div>
                    {city.region && <div className="text-xs text-gray-500">{city.region}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-teal-600">{formatNumber(city.sessions)}</div>
                    <div className="text-xs text-gray-500">sessions</div>
                  </div>
                </div>
              ))}
              {topCities.length === 0 && (
                <p className="text-gray-500 text-center py-4">No geographic data available</p>
              )}
            </div>
          </div>

          {/* User Type Comparison */}
          <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2">
            <h3 className="font-semibold text-gray-800 mb-4">👤 New vs Returning Visitors</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-4xl mb-2">✨</div>
                <div className="text-3xl font-bold text-green-600">{formatNumber(overview.new_users)}</div>
                <div className="text-gray-600">New Users</div>
                <div className="text-sm text-gray-500 mt-1">
                  {overview.total_users > 0
                    ? `${((overview.new_users / overview.total_users) * 100).toFixed(1)}%`
                    : '0%'} of total
                </div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl mb-2">🔄</div>
                <div className="text-3xl font-bold text-blue-600">{formatNumber(overview.returning_users)}</div>
                <div className="text-gray-600">Returning Users</div>
                <div className="text-sm text-gray-500 mt-1">
                  {overview.total_users > 0
                    ? `${((overview.returning_users / overview.total_users) * 100).toFixed(1)}%`
                    : '0%'} of total
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      {data?.retrieved_at && (
        <div className="text-center text-sm text-gray-500">
          Data retrieved: {new Date(data.retrieved_at).toLocaleString()} • GA4 Property: 303087554
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'border-l-blue-500 bg-blue-50',
    green: 'border-l-green-500 bg-green-50',
    purple: 'border-l-purple-500 bg-purple-50',
    orange: 'border-l-orange-500 bg-orange-50',
    teal: 'border-l-teal-500 bg-teal-50',
    pink: 'border-l-pink-500 bg-pink-50',
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${colorClasses[color] || ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
};

export default GA4Analytics;
